import type { ReactNode } from 'react';

/**
 * What a component renders when it has nothing to show.
 *
 * The package cannot import the constant the apps already declare — R1 keeps it dependency-free —
 * so it declares the same string and exports it. Call sites that agree with the default can then
 * leave `emptyValue` off entirely, which is most of them.
 */
export const DEFAULT_EMPTY_VALUE = '--';

export type EmptyProps = {
  /** Rendered in place of the value when there is nothing to show. */
  readonly emptyValue?: ReactNode;
};

/**
 * The three absent cases: `null`, `undefined` and `NaN`.
 *
 * `0` is deliberately not one of them. A vehicle that has travelled no distance, a reimbursement of
 * nothing and a tyre reading zero are all facts worth showing, and the `value || '--'` idiom that
 * spread through the features hides every one of them.
 *
 * The predicate names only `null | undefined` because that is the half worth having: on the false
 * side the value narrows to `number`, which is what every call site hands to a formatter. `NaN` is
 * a `number` at runtime and so does not belong to the type named on the true side — safe only
 * because a caller taking that branch renders `emptyValue` and never reads the value.
 */
export const isBlankNumber = (
  value: number | null | undefined
): value is null | undefined =>
  value === null || value === undefined || Number.isNaN(value);
